import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import classNames from 'classnames';
import Slider from 'react-slick';
import css from './SelectYourTutor.module.css';
import IconCard from '../../SavedCardDetails/IconCard/IconCard';
import Button from '../../Button/Button';
import { FormattedMessage } from '../../../util/reactIntl';
import NamedLink from '../../NamedLink/NamedLink';
import { getListingsById } from '../../../ducks/marketplaceData.duck';
import { searchListings } from '../../../containers/SearchPage/SearchPage.duck';
import { useConfiguration } from '../../../context/configurationContext';
import { convertMoneyToNumber } from '../../../util/currency';
import { convertUSDToSelected, getCurrencySymbol } from '../../../util/currencyConversion';

const PrevArrow = ({ onClick }) => (
  <button type="button" className={classNames(css.arrow, css.left)} onClick={onClick}>
    <IconCard type="prevArrow" />
  </button>
);

const NextArrow = ({ onClick }) => (
  <button type="button" className={classNames(css.arrow, css.right)} onClick={onClick}>
    <IconCard type="nextArrow" />
  </button>
);

const SelectYourTutor = ({ listings, onSearchListings }) => {
  const [slideCount, setSlideCount] = useState(1);
  const [screenWidth, setScreenWidth] = useState();
  const config = useConfiguration();
  const sliderRef = React.useRef();

  const selectedCurrency = useSelector(state => state?.currency?.selectedCurrency) || 'USD';
  const fxRates = useSelector(state => state?.currency?.fxRates);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      if (width >= 1440) {
        setSlideCount(4);
      } else if (width >= 1280) {
        setSlideCount(3.5);
      } else if (width >= 768) {
        setSlideCount(2.5);
      } else {
        setSlideCount(1);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Load search listings when component mounts
    if (config && listings.length === 0) {
      onSearchListings(
        {
          perPage: 6,
          pub_listingType: 'book-seats',
          include: ['author', 'images', 'author.profileImage'],
          'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
          'fields.image': ['variants.scaled-small', 'variants.scaled-medium'],
          'limit.images': 1,
        },
        config
      );
    }
  }, [config, listings.length, onSearchListings]);

  const settings = {
    // infinite: false,
    speed: 500,
    slidesToShow: slideCount,
    slidesToScroll: 1,
    centerPadding: 20,
    prevArrow: screenWidth > 600 ? <PrevArrow /> : null,
    nextArrow: screenWidth > 600 ? <NextArrow /> : null,
    arrows: screenWidth > 600,
  };

  // Transform listing data to match the expected format
  const transformListingToTutor = (listing) => {
    const author = listing?.author || {};
    const profile = author?.attributes?.profile || {};
    const publicData = listing?.attributes?.publicData || {};
    const price = listing?.attributes?.price || {};
    const imageUrl = listing?.author?.profileImage?.attributes?.variants?.["scaled-medium"]?.url || [];
    const imageVariant = imageUrl ? { url: imageUrl } : null;

    const baseUSD = price ? convertMoneyToNumber(price) : null;
    const conv = baseUSD != null ? convertUSDToSelected(baseUSD, selectedCurrency, fxRates) : null;
    const formattedPrice = conv ? conv.amount.toFixed(2) : '';
    const symbol = getCurrencySymbol(selectedCurrency).trim();

    return {
      id: listing.id.uuid,
      nameId: profile?.displayName,
      flag: publicData?.country,
      credits: formattedPrice,
      currencySymbol: symbol,
      rating: publicData?.ratingAvg,
      reviews: publicData?.reviewCount,
      img: imageVariant?.url || 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=600&q=80',
      descId: publicData?.aboutMe,
      listingId: listing.id.uuid,
    };
  };

  const tutorListings = listings.map(transformListingToTutor);

  return (
    <section className={css.sectionWrapper}>
      <div className={css.sectionHeading}>
        <h2 className={css.sectionTitle}><FormattedMessage id='SelectYourTutor.sectionHeaidng' /></h2>
        <p className={css.sectionInfo}><FormattedMessage id='SelectYourTutor.sectionInfo' /></p>
      </div>
      <div className={css.wrapper} id="selectYourTutor">
        <Slider ref={sliderRef} {...settings} className={css.slider}>
          {tutorListings?.map(tutor => (
            <div key={tutor.id} className={css.card}>
              <img src={tutor.img} alt="tutor" className={css.cardImage} />
              <div className={css.cardBody}>
                <div className={css.nameRow}>
                  <span className={css.name}>
                    {tutor.nameId}
                  </span>
                  <img style={{
                    display: 'inline-block',
                    width: '24px',
                    height: '18px',
                    verticalAlign: 'middle',
                  }} src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/${tutor.flag.toLowerCase()}.svg`} alt="flag" className={css.flag} />
                </div>

                <div className={css.statsRow}>
                  <div className={css.credits}>
                    <div>
                      <span className={css.value}>
                        {tutor.currencySymbol} {tutor.credits}
                      </span>
                    </div>

                    <span className={css.text}>
                      <FormattedMessage id="SelectYourTutor.lessonDuration" />
                    </span>
                  </div>
                  <div className={css.rating}>
                    <div className={css.ratingValue}>
                      <IconCard type="solidStar" />
                      <span className={css.value}>{tutor.rating}</span>
                    </div>

                    <span className={css.text}>
                      <FormattedMessage
                        id="SelectYourTutor.reviews"
                        values={{ count: tutor.reviews }}
                      />
                    </span>
                  </div>
                </div>
                <p className={css.desc}>
                  <FormattedMessage id={tutor.descId} />
                </p>
                <a target="_blank" rel="noopener noreferrer" href={`/l/${tutor?.nameId?.toLowerCase()?.replaceAll(' ', '-')}/${tutor.listingId}`} className={css.profileBtn}>
                  <FormattedMessage id="SelectYourTutor.viewProfile" /> <IconCard type="rightarrow" />
                </a>
              </div>
            </div>
          ))}
        </Slider>

        {screenWidth <= 600 && (
          <div className={css.mobileNav}>
            <button type="button" onClick={() => sliderRef.current?.slickPrev()}>
              <IconCard type="prevArrow" />
            </button>
            <button type="button" onClick={() => sliderRef.current?.slickNext()}>
              <IconCard type="nextArrow" />
            </button>
          </div>
        )}
        <div className={css.exploreMoreTutor}>
          <NamedLink name="SearchPage">
            <FormattedMessage id="BlogPage.exploreMore" />
          </NamedLink>
        </div>
      </div></section>
  );
};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.SearchPage || {};
  const listings = getListingsById(state, currentPageResultIds);
  return {
    listings,
  };
};

const mapDispatchToProps = dispatch => ({
  onSearchListings: (searchParams, config) => dispatch(searchListings(searchParams, config)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SelectYourTutor);
